import { useState } from 'react'
import toast from 'react-hot-toast'
import { useUpgradeBuilding, useTavernBlueprint, type BuildingDto, type ResourcesDto } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { useResourceTick } from '@/hooks/useResourceTick'
import { BuildingIcon, GoldIcon, WoodIcon, ChevronDown } from '@/components/game/GameIcons'
import { GoldButton } from '@/components/game/ui'
import { BuildingProjectionTable } from '@/components/BuildingProjectionTable'
import { TavernProgressionTable } from '@/components/TavernProgressionTable'
import { formatDuration } from '@/lib/time'

interface Props {
  building: BuildingDto
  resources: ResourcesDto
  hqLevel: number
  /** Fronts de chantier déjà ouverts sur l'île. */
  slotsUsed: number
  /** Créneaux accordés par le niveau de QG (1 en dessous du QG 18). */
  slots: number
}

const RES_ICON = { gold: GoldIcon, wood: WoodIcon } as const

export function BuildingCard({ building, resources, hqLevel, slotsUsed, slots }: Props) {
  const upgrade = useUpgradeBuilding()
  const countdown = useCountdown(building.upgradeResolvesAt)
  const [showProjection, setShowProjection] = useState(false)
  const isTavern = building.type === 'tavern'
  const { data: tavernBlueprint } = useTavernBlueprint(isTavern)

  const productions = Object.entries(building.productionPerHour)
  // Le prix qui compte est celui que le serveur débitera : majoré si un créneau
  // est déjà occupé. Le nominal reste affiché barré à côté, pour que le surcoût
  // se lise AVANT le clic et non après.
  const cost = building.nextUpgradePremiumCost
  const isPremium = building.slotPremiumMultiplier > 1

  // Gold/wood accrue continuously server-side; mirror that client-side so a
  // build blocked on "ressources insuffisantes" re-enables itself as they tick
  // up — no manual refresh needed.
  const liveGold = useResourceTick(resources.gold, resources.goldPerHour, resources.capacity)
  const liveWood = useResourceTick(resources.wood, resources.woodPerHour, resources.capacity)
  const liveResources = { ...resources, gold: liveGold, wood: liveWood }

  const gate = computeGate(building, liveResources, hqLevel, slotsUsed, slots)

  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-lg text-sand-300 flex items-center gap-2">
            <BuildingIcon type={building.type} className="w-5 h-5 text-sand-300" />
            {building.typeLabel}
          </h3>
          <p className="text-xs text-ocean-300 mt-0.5">{building.description}</p>
        </div>
        <span className="shrink-0 text-xs font-semibold bg-ocean-900/60 text-sand-100 rounded px-2 py-1">
          Niv. {building.level}
        </span>
      </div>

      {productions.length > 0 && (
        <div className="text-sm text-ocean-100 flex items-center gap-1.5 flex-wrap">
          Production :
          {productions.map(([res, rate]) => (
            <span key={res} className="text-sand-300 font-medium">{rate}/h {RESOURCE_LABEL[res] ?? res}</span>
          ))}
        </div>
      )}

      {building.storageCapacity > 0 && (
        <div className="text-sm text-ocean-100">
          Capacité : {building.storageCapacity.toLocaleString('fr-FR')}
        </div>
      )}

      <div className="mt-auto pt-2 border-t border-sand-500/12">
        {building.isUpgrading ? (
          <div className="bg-ocean-900/60 rounded p-2 text-center">
            {/* Niveau 0 = construction initiale (ex. déblocage de la Taverne) ;
                niveau 1+ = amélioration. On réutilise le terme « Construction en
                cours » des défenses (DefensePanel) ; ici on distingue en plus
                l'amélioration (DefensePanel garde « Construction » à tout niveau). */}
            <p className="text-xs text-ocean-300 uppercase tracking-wider">
              {building.level === 0 ? 'Construction en cours' : 'Amélioration en cours'}
            </p>
            <p className="font-display text-lg text-sand-100 tabular-nums">{countdown}</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-ocean-300 mb-2 flex items-center gap-1.5 flex-wrap">
              Niv. {building.level + 1} :
              {isPremium && <Struck amount={building.nextUpgradeCost.gold} />}
              <Cost have={liveResources.gold} need={cost.gold} kind="gold" premium={isPremium} />
              {isPremium && <Struck amount={building.nextUpgradeCost.wood} />}
              <Cost have={liveResources.wood} need={cost.wood} kind="wood" premium={isPremium} />
              {isPremium && (
                <span className="inline-flex items-center rounded border border-amber-400/50 bg-amber-400/10 px-1 py-px text-[0.65rem] font-bold tabular-nums text-amber-300">
                  ×{formatMultiplier(building.slotPremiumMultiplier)}
                </span>
              )}
              · ⏱ {formatDuration(building.nextUpgradeTimeSeconds)}
            </p>
            <UpgradeGain building={building} />
            <GoldButton
              onClick={() =>
                upgrade.mutate(building.id, {
                  onSuccess: () => toast.success(`${building.typeLabel} : amélioration lancée !`),
                  onError: (err) => toast.error(err.message || 'Amélioration impossible'),
                })
              }
              disabled={!gate.canUpgrade || upgrade.isPending}
              title={gate.reason ?? "Lancer l'amélioration"}
              className="w-full text-sm px-3 py-1.5"
            >
              {upgrade.isPending ? 'Lancement…' : gate.canUpgrade ? 'Améliorer' : gate.shortLabel}
            </GoldButton>
          </>
        )}
      </div>

      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowProjection((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-ocean-300 hover:text-sand-300 transition-colors"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showProjection ? 'rotate-180' : ''}`} />
          {showProjection ? "Masquer l'évolution" : "Voir l'évolution par niveau"}
        </button>
        {showProjection && (
          isTavern
            ? (tavernBlueprint
                ? <TavernProgressionTable progression={tavernBlueprint.progression} currentLevel={building.level} />
                : <p className="text-xs text-ocean-300 py-2">Chargement de l'évolution…</p>
              )
            : <BuildingProjectionTable type={building.type} currentLevel={building.level} />
        )}
      </div>
    </div>
  )
}

const RESOURCE_LABEL: Record<string, string> = { gold: 'Or', wood: 'Bois', rum: 'Rhum' }

/** "Before → after" production (or storage) gain for the next level. */
function UpgradeGain({ building }: { building: BuildingDto }) {
  const prod = Object.entries(building.productionPerHour)
  if (prod.length > 0) {
    const [res, cur] = prod[0]
    const next = building.nextLevelProductionPerHour[res] ?? cur
    const gain = next - cur
    return (
      <p className="text-xs text-sand-300/90 mb-2">
        <span className="ml-1 font-semibold text-green-300">+{gain}/h</span>
      </p>
    )
  }
  if (building.storageCapacity > 0) {
    const gain = building.nextLevelStorageCapacity - building.storageCapacity
    return (
      <p className="text-xs text-sand-300/90 mb-2">
        <span className="ml-1 font-semibold text-green-300">+{gain.toLocaleString('fr-FR')}</span>
      </p>
    )
  }
  return null
}

function Cost({
  have,
  need,
  kind,
  premium = false,
}: {
  have: number
  need: number
  kind: 'gold' | 'wood'
  premium?: boolean
}) {
  const enough = have >= need
  const Icon = RES_ICON[kind]
  const tone = !enough ? 'text-red-400' : premium ? 'text-amber-300 font-semibold' : 'text-sand-300'
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums ${tone}`}>
      <Icon className="w-3.5 h-3.5" /> {need.toLocaleString('fr-FR')}
    </span>
  )
}

/** Prix nominal conservé barré à côté du prix majoré, pour rendre l'écart lisible. */
function Struck({ amount }: { amount: number }) {
  return <span className="line-through tabular-nums text-ocean-100/45">{amount.toLocaleString('fr-FR')}</span>
}

/** 1.5 → « 1,5 » ; 2 → « 2 » (pas de décimale inutile, virgule française). */
function formatMultiplier(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toLocaleString('fr-FR')
}

interface Gate {
  canUpgrade: boolean
  reason: string | null
  shortLabel: string
}

function computeGate(
  building: BuildingDto,
  resources: ResourcesDto,
  hqLevel: number,
  slotsUsed: number,
  slots: number,
): Gate {
  if (slotsUsed >= slots) {
    // Un joueur à un seul créneau (tout ce qui est sous le QG 18) garde le
    // libellé historique : pour lui, la mécanique de créneaux n'existe pas encore.
    return slots <= 1
      ? { canUpgrade: false, reason: 'Une construction est déjà en cours', shortLabel: 'File occupée' }
      : {
          canUpgrade: false,
          reason: `Tes ${slots} créneaux de chantier sont occupés`,
          shortLabel: 'Créneaux occupés',
        }
  }

  // Non-HQ buildings may lead the HQ by HQ_GATE_GAP levels before the gate
  // kicks in (keep in sync with BuildingUpgradeService::HQ_GATE_GAP).
  const HQ_GATE_GAP = 2
  const targetLevel = building.level + 1
  if (building.type !== 'hq' && targetLevel > hqLevel + HQ_GATE_GAP) {
    const requiredHq = targetLevel - HQ_GATE_GAP
    return {
      canUpgrade: false,
      reason: `Quartier-Général niveau ${requiredHq} requis`,
      shortLabel: `QG niv. ${requiredHq} requis`,
    }
  }

  // On mesure contre le prix MAJORÉ, celui que le serveur débitera. Comparer au
  // nominal laisserait cliquer un bouton qui repart en 409.
  const canAfford =
    resources.gold >= building.nextUpgradePremiumCost.gold &&
    resources.wood >= building.nextUpgradePremiumCost.wood
  if (!canAfford) {
    return { canUpgrade: false, reason: 'Ressources insuffisantes', shortLabel: 'Ressources insuffisantes' }
  }

  return { canUpgrade: true, reason: null, shortLabel: 'Améliorer' }
}
